<template>
  <table class="revisions-table">
    <thead>
      <tr>
        <th class="col-when" :class="{ wide: !groupByDay }">When</th>
        <th v-if="showRecord" class="col-record">Record</th>
        <th class="col-field">Field</th>
        <th>Change</th>
        <th class="col-user">Changed by</th>
        <th class="col-expand"></th>
      </tr>
    </thead>
    <tbody>
      <template v-for="row in rows" :key="row.key">
        <tr v-if="row.day" class="day-row">
          <td :colspan="columnCount">{{ row.day }}</td>
        </tr>
        <template v-else>
          <tr class="change-row" :class="{ expandable: isExpandable(row.item), expanded: expanded[row.item.id] }"
            @click="isExpandable(row.item) && toggleExpanded(row.item.id)">
            <td v-if="groupByDay" class="col-when" :title="formatTimestamp(row.item.time)">
              {{ formatTime(row.item.time) }}
              <div class="muted small">{{ relativeTime(row.item.time, now) }}</div>
            </td>
            <td v-else class="col-when wide" :title="relativeTime(row.item.time, now)">
              {{ formatTimestamp(row.item.time) }}
            </td>
            <td v-if="showRecord" class="col-record">
              <button v-if="linkable" type="button" class="record-link" title="Show only this record"
                @click.stop="$emit('focus-record', row.item)">
                <span class="type-badge" :style="typeStyle(row.item.revisionable_type)">
                  {{ row.item.revisionable_type }}
                </span>
                #{{ row.item.revisionable_id }}
              </button>
              <template v-else>
                <span class="type-badge" :style="typeStyle(row.item.revisionable_type)">
                  {{ row.item.revisionable_type }}
                </span>
                #{{ row.item.revisionable_id }}
              </template>
              <div v-if="row.item.label" class="muted small record-label" :title="row.item.label">
                {{ row.item.label }}
              </div>
            </td>
            <td class="col-field">
              <span v-if="row.item.action !== 'updated'" class="action-badge" :class="row.item.action">
                {{ row.item.action }}
              </span>
              <code v-else>{{ row.item.key }}</code>
            </td>
            <td>
              <ChangeSummary :item="row.item" />
            </td>
            <td class="col-user">
              <button v-if="linkable" type="button" class="user-link" title="Show only changes by this user"
                @click.stop="$emit('focus-user', row.item)">{{ row.item.user_name || 'System' }}</button>
              <template v-else>{{ row.item.user_name || 'System' }}</template>
              <div class="muted small">{{ roleDisplay(row.item) }}</div>
            </td>
            <td class="col-expand">
              <font-awesome-icon v-if="isExpandable(row.item)" class="expand-icon"
                :icon="['fas', expanded[row.item.id] ? 'chevron-up' : 'chevron-down']" />
            </td>
          </tr>
          <tr v-if="expanded[row.item.id]" class="detail-row">
            <td :colspan="columnCount">
              <ChangeDetail :item="row.item" />
            </td>
          </tr>
        </template>
      </template>
    </tbody>
  </table>
</template>

<script>
import { h } from 'vue';
import {
  isEmptyValue, preview, isExpandable, roleDisplay, typeStyle,
  formatTimestamp, formatTime, dayLabel, relativeTime,
} from './revisionUtils';

// One-line description of a change, shown in every row
const ChangeSummary = {
  name: 'ChangeSummary',
  props: { item: { type: Object, required: true } },
  setup(props) {
    return () => {
      const item = props.item;
      if (item.action !== 'updated') {
        const snapshot = item.snapshot;
        const fields = snapshot ? Object.keys(snapshot).length : 0;
        const label = item.action === 'created' ? 'Record created' : 'Record deleted';
        return h('span', { class: 'muted' }, snapshot ? `${label} · ${fields} fields` : label);
      }
      const value = (v, cls) => isEmptyValue(v)
        ? h('span', { class: 'value empty' }, 'empty')
        : h('span', { class: `value ${cls}` }, preview(v));
      return h('span', { class: 'change-summary' }, [
        value(item.old_value, 'old'),
        h('span', { class: 'arrow', 'aria-label': 'changed to' }, '→'),
        value(item.new_value, 'new'),
      ]);
    };
  },
};

// Full values for an expanded row
const ChangeDetail = {
  name: 'ChangeDetail',
  props: { item: { type: Object, required: true } },
  setup(props) {
    return () => {
      const item = props.item;
      if (item.snapshot) {
        return h('dl', { class: 'snapshot' }, Object.entries(item.snapshot).flatMap(([key, value]) => [
          h('dt', key),
          h('dd', isEmptyValue(value) ? h('span', { class: 'value empty' }, 'empty')
            : typeof value === 'object' ? JSON.stringify(value) : String(value)),
        ]));
      }
      const block = (label, value, cls) => h('div', { class: 'detail-block' }, [
        h('div', { class: 'detail-label' }, label),
        isEmptyValue(value)
          ? h('span', { class: 'value empty' }, 'empty')
          : h('pre', { class: `detail-value ${cls}` }, String(value)),
      ]);
      return h('div', { class: 'detail-grid' }, [
        block('Before', item.old_value, 'old'),
        block('After', item.new_value, 'new'),
      ]);
    };
  },
};

// Table of normalised revisions (see normaliseRevisions) with expandable before/after details
export default {
  name: 'RevisionTable',
  components: {
    ChangeSummary,
    ChangeDetail,
  },

  props: {
    items: { type: Array, required: true },
    now: { type: Number, default: () => Date.now() },
    // Show the record type / ID column (off when every row is the same record)
    showRecord: { type: Boolean, default: false },
    // Insert a heading row wherever the day changes
    groupByDay: { type: Boolean, default: false },
    // Make record and user names clickable, emitting focus-record / focus-user
    linkable: { type: Boolean, default: false },
  },
  emits: ['focus-record', 'focus-user'],

  data() {
    return {
      expanded: {},
    };
  },

  computed: {
    columnCount() {
      return this.showRecord ? 6 : 5;
    },
    rows() {
      if (!this.groupByDay) return this.items.map(item => ({ key: item.id, item }));
      const rows = [];
      let lastDay = null;
      this.items.forEach(item => {
        const day = dayLabel(item.time, this.now);
        if (day !== lastDay) {
          rows.push({ key: `day-${item.id}`, day });
          lastDay = day;
        }
        rows.push({ key: item.id, item });
      });
      return rows;
    },
  },

  methods: {
    isExpandable,
    roleDisplay,
    typeStyle,
    formatTimestamp,
    formatTime,
    relativeTime,
    toggleExpanded(id) {
      this.expanded = { ...this.expanded, [id]: !this.expanded[id] };
    },
  },
};
</script>

<style scoped>
.revisions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  text-align: left;
}

.revisions-table th {
  background: #f4f4f4;
  text-align: left;
  padding: 8px 10px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #555;
  border: none;
  border-bottom: 1px solid #ddd;
}

.revisions-table td {
  padding: 8px 10px;
  border: none;
  border-bottom: 1px solid #eee;
  vertical-align: top;
  text-align: left;
}

.muted {
  color: #6b7280;
}

.small {
  font-size: 12px;
}

.col-when {
  width: 110px;
  white-space: nowrap;
}

.col-when.wide {
  width: 160px;
}

.col-record {
  width: 200px;
  white-space: nowrap;
}

.col-field {
  width: 170px;
}

.col-user {
  width: 200px;
}

.col-expand {
  width: 32px;
  text-align: center;
}

.day-row td {
  background: #fafafa;
  font-weight: 600;
  font-size: 13px;
  color: #463333;
  padding: 6px 10px;
  border-bottom: 1px solid #e5e5e5;
}

.change-row.expandable {
  cursor: pointer;
}

.change-row:hover {
  background: #f8f9fb;
}

.change-row.expanded {
  background: #f3f5f8;
}

.detail-row td {
  background: #f9fafb;
  padding: 12px 16px;
}

.expand-icon {
  color: #888;
}

code {
  font-size: 13px;
  background: #f1f3f5;
  padding: 1px 6px;
  border-radius: 4px;
  color: #333;
  word-break: break-word;
}

.record-link,
.user-link {
  border: none;
  background: none;
  padding: 0;
  text-align: left;
  color: inherit;
}

.record-link:hover,
.user-link:hover {
  text-decoration: underline;
}

.record-label {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-badge {
  --type-hue: 0;
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 4px;
  margin-right: 4px;
  color: hsl(var(--type-hue), 55%, 28%);
  background: hsl(var(--type-hue), 70%, 93%);
  border: 1px solid hsl(var(--type-hue), 45%, 80%);
}

.action-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 7px;
  border-radius: 4px;
}

.action-badge.created {
  background: #e6f4ea;
  color: #1e6b34;
}

.action-badge.deleted {
  background: #fdecea;
  color: #a1261b;
}

:deep(.change-summary) {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  word-break: break-word;
}

:deep(.value.old) {
  color: #b42318;
  text-decoration: line-through;
  text-decoration-color: rgba(180, 35, 24, 0.4);
}

:deep(.value.new) {
  color: #1e6b34;
}

:deep(.value.empty) {
  color: #999;
  font-style: italic;
}

:deep(.arrow) {
  color: #999;
}

:deep(.detail-grid) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

:deep(.detail-label) {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #666;
  margin-bottom: 4px;
}

:deep(.detail-value) {
  margin: 0;
  padding: 8px 10px;
  border-radius: 5px;
  background: #fff;
  border: 1px solid #e3e3e3;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  max-height: 300px;
  overflow: auto;
}

:deep(.detail-value.old) {
  border-left: 3px solid #e5a29b;
}

:deep(.detail-value.new) {
  border-left: 3px solid #96cfa6;
}

:deep(.snapshot) {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 4px 16px;
  margin: 0;
  font-size: 13px;
}

:deep(.snapshot dt) {
  font-weight: 600;
  color: #555;
}

:deep(.snapshot dd) {
  margin: 0;
  word-break: break-word;
}

@media (max-width: 800px) {
  :deep(.detail-grid) {
    grid-template-columns: 1fr;
  }
}
</style>
